import { useEffect } from 'react';

import { batch } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import NotFound from '@/components/NotFound';
import { useIsApplicationsAndDataEnabled } from '@/hooks/useIsApplicationsAndDataEnabled';
import useIsMultipleTeamsEnabled from '@/hooks/useIsMultipleTeamsEnabled';
import useIsOrgNavBar2025Enabled from '@/hooks/useIsOrgNavBar2025Enabled';
import useIsTeamsEnabled from '@/hooks/useIsTeamsEnabled';
import useShowSuperuserUI from '@/hooks/useShowSuperuserUI';
import useUser from '@/hooks/useUser';
import Modals from '@/legacy/Modal';
import Query from '@/legacy/Query';
import { refreshDataSources } from '@/legacy/store/actions/DataSourceActions';
import { fetchWorkflowsMetadata } from '@/legacy/store/actions/WorkflowsAsyncActions';
import { fetchOntology } from '@/legacy/store/actions/udm/UdmActions';
import { useAppDispatch } from '@/legacy/store/hooks';
import { fetchOnboardingChecklist } from '@/legacy/store/slices/entities/OnboardingChecklist';
import { fetchQueries } from '@/legacy/store/slices/entities/Queries';
import { BasetenPageEnum, findMatchingPageFromPath, renderRoutes } from '@/routes';
import PaymentMethodDialog from '@/sections/Billing/PaymentMethodDialog';
import BillBar from '@/sections/Billip/BillBar/lazy';
import { ReleaseEnv } from '@/types/releaseEnv';
import { useSegmentIdentify, useSegmentPageViews } from '@/utils/analytics/segment/hooks';
import { usePylon } from '@bolasim/react-use-pylon';

import PageComponents from './PageComponents';
import Routes from './routes';
import { Container, Content } from './styled';

function Creator() {
  const user = useUser();
  const dispatch = useAppDispatch();
  useSegmentIdentify();
  useSegmentPageViews();
  const { update: updatePylon } = usePylon();
  const isApplicationsAndDataEnabled = useIsApplicationsAndDataEnabled();
  const isOrgNavBar2025Enabled = useIsOrgNavBar2025Enabled();
  const showSuperuserUI = useShowSuperuserUI();
  const location = useLocation();
  const page = findMatchingPageFromPath(location.pathname);
  const isTeamsEnabled = useIsTeamsEnabled();
  const isMultipleTeamsEnabled = useIsMultipleTeamsEnabled();

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    batch(() => {
      if (isApplicationsAndDataEnabled) {
        dispatch(fetchWorkflowsMetadata(ReleaseEnv.Draft));
        dispatch(fetchQueries());
        dispatch(refreshDataSources());
      }
      dispatch(fetchOnboardingChecklist());

      if (page !== BasetenPageEnum.Onboarding) {
        dispatch(fetchOntology());
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (ENV_CONFIG.PYLON_APP_ID) {
      // Update Pylon with user's information
      updatePylon({
        name: [user.firstName, user.lastName].join(' '),
        email: user.email,
      });
    }
  }, [user]);

  if (!isMultipleTeamsEnabled) {
    PageComponents[BasetenPageEnum.TeamSettings] = () => (
      <NotFound sx={{ background: 'transparent' }} />
    );
  }

  return (
    <Container>
      <Content>
        {showSuperuserUI && <BillBar />}
        <Switch>
          {/* The following redirects are needed to support the old model managment routes */}
          <Redirect from="/models/:model_id/versions/:version_id" to="/models/:model_id" exact />
          <Redirect
            from="/models/:model_id/versions/:version_id/overview"
            to="/models/:model_id"
            exact
          />
          <Redirect
            from="/models/:model_id/versions/:version_id/:tab_id"
            to="/models/:model_id/:tab_id/:version_id"
          />
          {isTeamsEnabled && [
            <Redirect key="redirect-models" from="/models" to="/overview" exact />,
            <Redirect key="redirect-chains" from="/chains" to="/overview" exact />,
            <Redirect key="redirect-training" from="/training" to="/overview" exact />,
            <Redirect key="redirect-model-apis" from="/model-apis" to="/overview" exact />,
          ]}
          {/* ============================== */}
          {renderRoutes(Routes, PageComponents)}
          <Redirect from="/" to={isOrgNavBar2025Enabled ? '/overview' : '/models'} />
          <Redirect from="/data" to="/data/database_models" />
          <Route component={NotFound} />
        </Switch>
      </Content>
      <Modals />
      <Query />
      <PaymentMethodDialog />
    </Container>
  );
}

export default Creator;
